import httpService from "../../../../services/httpService";

const getOffsetTime = () => {
  const offset = new Date().getTimezoneOffset();
  const sign = offset > 0 ? '-' : '+';
  const abs = Math.abs(offset);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

export interface CandidateHighlight {
  candidate_id: string;
  name: string;
  key_qualifications: string[];
  why_match: string;
}

export interface ResumeSearchSummary {
  overall_assessment: string;
  top_candidates_highlights: CandidateHighlight[];
  key_strengths_found: string[];
  skill_gaps: string[];
  experience_levels: string;
  recommendation: string;
}

export type JobMatchType = "perfect" | "partial" | "mentioned" | "no_match";

export interface SkillMatchDetail {
  skill: string;
  match_status: JobMatchType;
  reason?: string;
}

export interface EducationTimelineItem {
  institution: string;
  degree: string;
  score: string;
  startDate?: string;
  endDate?: string;
}

export interface ExperienceTimelineItem {
  role: string;
  organization: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  responsibilities?: string[];
  impact_metrics?: string[];
  skills?: string[];
}

export interface ProjectsTimelineItem {
  name: string;
  description?: string;
  impact_metrics?: string[];
  keywords?: string[];
  skills?: string[];
}

export interface JobSearchResultCandidate {
  candidate_id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  education: string;
  resume_url: string;
  match_score: number;
  relevance_reason?: string;
  skills?: string[];
  year_of_experience?: number | null;
  education_timeline?: EducationTimelineItem[];
  experience_timeline?: ExperienceTimelineItem[];
  projects_timeline?: ProjectsTimelineItem[];
  skill_match?: JobMatchType;
  matched_skills?: string[];
  missing_skills?: string[];
  skill_matches?: SkillMatchDetail[];
  skills_score?: number;
  skills_reason?: string;
  experience_score?: number;
  experience_reason?: string;
  education_score?: number;
  education_reason?: string;
  proficiency_score?: number;
  proficiency_reason?: string;
}

export interface JobSearchResponse {
  results: JobSearchResultCandidate[];
  total_results: number;
  generated_summary?: ResumeSearchSummary;
  message?: string;
}

export interface JobSearchHistory {
  search_id: string;
  company_id: string;
  title: string;
  job_description: string;
  searched_by: string;
  search_date: string;
  status?: string;
  error?: string | null;
  message?: string;
  results: JobSearchResponse;
  opportunity_id?: string | null;
}

export interface JobSearchListResponse {
  records: JobSearchHistory[];
  total_no_of_records: number;
}

export interface JobSearchStatusResponse {
  search_id: string;
  status: string;
  progress_percent?: number;
  progress_message?: string;
  error?: string | null;
}

export interface ProspectiveCandidatesResponse {
  status: "NOT_STARTED" | "PROCESSING" | "COMPLETE" | "FAILED";
  search_id: string | null;
  search_date?: string;
  candidates: JobSearchResultCandidate[];
  progress_percent?: number;
  progress_message?: string;
  total_results?: number;
  message?: string;
}

class JobSearchClient {
  private basePath = "/interview_admin/job_search";
  private jobOpportunityPath = "/job_opportunities";

  async createJobSearch(data: { title: string; job_description: string; top_k?: number; generate_summary?: boolean }): Promise<JobSearchHistory> {
    const response = await httpService.post<JobSearchHistory>(this.basePath, data);
    return response.data;
  }

  async getJobSearchStatus(searchId: string): Promise<JobSearchStatusResponse> {
    const response = await httpService.get<JobSearchStatusResponse>(`${this.basePath}/${searchId}/status`);
    return response.data;
  }

  async listJobSearches(): Promise<JobSearchListResponse> {
    const offsetTime = getOffsetTime();
    const response = await httpService.get<JobSearchListResponse>(this.basePath, {
      params: { offset_time: offsetTime }
    });
    return response.data;
  }

  async getJobSearch(searchId: string): Promise<JobSearchHistory> {
    const offsetTime = getOffsetTime();
    const response = await httpService.get<JobSearchHistory>(`${this.basePath}/${searchId}`, {
      params: { offset_time: offsetTime }
    });
    return response.data;
  }

  async regenerateJobSearch(searchId: string): Promise<JobSearchHistory> {
    const response = await httpService.put<JobSearchHistory>(`${this.basePath}/${searchId}/regenerate`);
    return response.data;
  }

  // ─── Per-opportunity talent search & prospective candidates ───

  async getProspectiveCandidates(opportunityId: string): Promise<ProspectiveCandidatesResponse> {
    const response = await httpService.get<ProspectiveCandidatesResponse>(
      `${this.jobOpportunityPath}/${opportunityId}/prospective_candidates`
    );
    return response.data;
  }

  async addProspectiveCandidate(opportunityId: string, candidateId: string): Promise<any> {
    const response = await httpService.post<any>(
      `${this.jobOpportunityPath}/${opportunityId}/prospective_candidates/${candidateId}`
    );
    return response.data;
  }



  async getOpportunityTalentSearchStatus(opportunityId: string): Promise<JobSearchStatusResponse & { status: string }> {
    const response = await httpService.get<JobSearchStatusResponse & { status: string }>(
      `${this.jobOpportunityPath}/${opportunityId}/talent_search/status`
    );
    return response.data;
  }
}

const client = new JobSearchClient();
export default client;
